import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAdminReports } from '@/api/ads/hooks';
import { ResourceGate } from '@/features/ResourceGate';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Stat } from '@/components/ui/stat';
import { Table, Th, Td, EmptyRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { npr, num, pct } from '@/lib/format';

export function AdminReportsPage() {
  const { data, isLoading } = useAdminReports();

  const chart = (data?.revenue_by_product ?? []).map((r) => ({
    name: r.product__name,
    revenue: Number(r.revenue ?? 0),
  }));

  return (
    <ResourceGate slug="ads.reports">
      <PageHeader title="Ads Reports" description="Revenue and delivery across the platform." />
      {isLoading ? (
        <Spinner label="Loading reports…" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Total revenue" value={npr(data?.total_revenue)} />
            <Stat label="Impressions" value={num(data?.impressions)} />
            <Stat label="Clicks" value={num(data?.clicks)} />
            <Stat label="CTR" value={pct(data?.ctr_pct ?? 0)} />
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <Card>
              <CardHeader title="Revenue by product" />
              <CardBody>
                {chart.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No revenue yet.</p>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chart} layout="vertical" margin={{ left: 24 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis type="number" fontSize={12} />
                        <YAxis type="category" dataKey="name" width={120} fontSize={11} />
                        <Tooltip formatter={(v) => npr(v as number)} />
                        <Bar dataKey="revenue" fill="hsl(349 89% 51%)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Top advertisers" />
              <CardBody className="p-0">
                <Table>
                  <thead>
                    <tr>
                      <Th>Advertiser</Th>
                      <Th>Spend</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.top_advertisers.length ?? 0) === 0 && <EmptyRow colSpan={2}>No spend yet.</EmptyRow>}
                    {data?.top_advertisers.map((a, i) => (
                      <tr key={i}>
                        <Td className="font-medium">{a.wallet__advertiser__name || '—'}</Td>
                        <Td>{npr(a.spend)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </ResourceGate>
  );
}
